import { sortBy } from 'lodash';

import {
  buildOrgWeightPathKey,
  orgWeightPathSegment,
} from 'js/utils/org-utils';
import { normalizeTeamLineId } from 'js/utils/org-trends';

function pathSegment(node, type, pathKey) {
  return {
    pathKey,
    depthId: pathKey,
    isRoot: type === 'org' && Boolean(node.is_root),
    name: node.name,
    id: node.id,
    type,
  };
}

function breadcrumbFromPath(pathToNode, node) {
  return [...pathToNode.map((segment) => segment.name), node.name].join(' › ');
}

/**
 * Pre-order flatten of the API organization tree for the org node picker.
 * Each row carries the API node reference for scoreNode rollup.
 * rowKey is the full placement path (o:id/t:id/…) so duplicate
 * team or org ids under different parents stay unique.
 */
export function flattenOrgPickerRows(organization) {
  if (!organization) {
    return [];
  }

  const rows = [];

  function walk(node, ancestorPath, type, parentPathKey) {
    const isRoot = type === 'org' && Boolean(node.is_root);
    const rowKey = buildOrgWeightPathKey(
      parentPathKey,
      orgWeightPathSegment(type, node.id)
    );

    rows.push({
      node,
      pathToNode: ancestorPath,
      breadcrumb: breadcrumbFromPath(ancestorPath, node),
      type,
      depth: ancestorPath.length,
      isRoot,
      rowKey,
      pathKey: rowKey,
      id: node.id,
      name: node.name,
    });

    if (type === 'team') {
      return;
    }

    const childAncestors = [...ancestorPath, pathSegment(node, type, rowKey)];

    for (const child of sortBy(node.child_orgs ?? [], (o) =>
      o.name?.toUpperCase()
    )) {
      walk(child, childAncestors, 'org', rowKey);
    }

    for (const team of sortBy(node.teams ?? [], (t) => t.name?.toUpperCase())) {
      walk(team, childAncestors, 'team', rowKey);
    }
  }

  walk(organization, [], 'org', '');
  return rows;
}

export function filterOrgPickerRowsByName(rows, query) {
  const q = query.trim().toLowerCase();
  if (!q) {
    return rows;
  }

  return rows.filter((row) => row.name.toLowerCase().includes(q));
}

/** Breadcrumb items for ButtonBreadcrumbs (ancestors only, excluding selected row). */
export function rowBreadcrumbItems(row, rowsByKey) {
  if (!row?.pathToNode?.length) {
    return [];
  }

  return row.pathToNode
    .map((segment) => {
      const ancestorRow = rowsByKey.get(segment.pathKey);

      if (!ancestorRow) {
        return null;
      }

      return {
        label: segment.name,
        to: ancestorRow,
      };
    })
    .filter(Boolean);
}

export function rowsByKey(rows) {
  return new Map(rows.map((row) => [row.rowKey, row]));
}

function collectTeamIdsFromOrgNode(node) {
  const ids = new Set();

  const walk = (orgNode) => {
    for (const team of orgNode.teams ?? []) {
      ids.add(normalizeTeamLineId(team.id));
    }

    for (const childOrg of orgNode.child_orgs ?? []) {
      walk(childOrg);
    }
  };

  walk(node);
  return ids;
}

export function subOrgIdForPickerRow(row) {
  if (!row) {
    return null;
  }

  if (row.type === 'team') {
    const parentOrg = row.pathToNode?.[row.pathToNode.length - 1];
    return parentOrg?.id ?? null;
  }

  return row.id ?? row.node?.id ?? null;
}

export function resolvePickerRowFromUrl(pickerRows, subOrgId, teamId) {
  if (!pickerRows.length) {
    return null;
  }

  if (teamId != null) {
    return (
      pickerRows.find(
        (row) =>
          row.type === 'team' &&
          normalizeTeamLineId(row.id) === normalizeTeamLineId(teamId) &&
          subOrgIdForPickerRow(row) === subOrgId
      ) ??
      pickerRows.find((row) => row.type === 'org' && row.id === subOrgId) ??
      pickerRows.find((row) => row.isRoot) ??
      pickerRows[0]
    );
  }

  return (
    pickerRows.find((row) => row.type === 'org' && row.id === subOrgId) ??
    pickerRows.find((row) => row.isRoot) ??
    pickerRows[0]
  );
}

function normalizedIdFromTeamLike(team) {
  return normalizeTeamLineId(team?.teamId ?? team?.team_id ?? team?.id);
}

export function filterTeamsByPickerRow(teams, row) {
  if (!row || !Array.isArray(teams)) {
    return teams ?? [];
  }

  if (row.type === 'team') {
    const teamId = normalizeTeamLineId(row.id ?? row.node?.id);

    return teams.filter((team) => normalizedIdFromTeamLike(team) === teamId);
  }

  const allowedTeamIds = collectTeamIdsFromOrgNode(row.node);

  return teams.filter((team) =>
    allowedTeamIds.has(normalizedIdFromTeamLike(team))
  );
}

/** Question-score rows from the org API (`team_id`), scoped to a picker node. */
export function filterQuestionScoresByPickerRow(questionScores, row) {
  return filterTeamsByPickerRow(questionScores, row);
}
